import axios from "axios";
import axiosInstance from "utils/axios";

// Anonymous, token-scoped counterpart to jobSelectionService.js — backs the public
// /client-selection-approval/:token page. Every call here goes through the shared axiosInstance
// (same as resolveQRCode/submitForm elsewhere in this app) without requiring a logged-in user;
// the backend resolves the job/category purely from the token.
//
// The token is sent as a ?token= query param, not a URL path segment — its length (Data
// Protection-encrypted payload) varies and can exceed IIS/IIS Express's ~260-char per-segment
// limit, which rejects the request before ASP.NET Core ever sees it and shows up in the browser
// as a misleading CORS error.

const unwrap = (response, fallbackMessage) => {
  if (response.data?.statusCode === 200 && response.data?.status === "Success") {
    return { success: true, data: response.data.data, message: response.data.message };
  }
  const err = new Error(response.data?.message || fallbackMessage);
  err.statusCode = response.data?.statusCode;
  throw err;
};

// Fetches the confirmed selections (with resolved, ready-to-display photo URLs) for the job +
// main category a token was minted for, plus whether that category is already signed/locked.
export const getClientSelectionApproval = async (token) => {
  try {
    const response = await axiosInstance.get(`api/ClientSelectionApproval`, {
      params: { token },
      showLoader: false,
    });
    const result = unwrap(response, "Failed to load selections");
    return { success: true, data: result.data };
  } catch (error) {
    console.error("Error in getClientSelectionApproval:", error);
    return {
      success: false,
      data: null,
      statusCode: error.statusCode || error.response?.status,
      error: error.response?.data?.message || error.message || "Failed to load selections",
    };
  }
};

// PUTs a raw file straight to S3 using a presigned URL — bare axios (not axiosInstance) since this
// request must NOT carry our API's baseURL/auth headers. Mirrors formBuilderService.js's
// putFileToPresignedUrl.
const putFileToPresignedUrl = (file, uploadUrl, contentType) =>
  axios.put(uploadUrl, file, {
    headers: { "Content-Type": contentType || "application/octet-stream" },
  });

// Uploads the client-side-generated signed PDF (a Blob, not a File — see
// ClientSelectionApprovalPage's PDF export step) via the presigned-upload flow, scoped to this
// token's job/company. Returns the fields lockClientSelectionApproval needs to finish the lock.
export const uploadClientSignedDocument = async (token, pdfBlob, fileName) => {
  const contentType = pdfBlob.type || "application/pdf";

  const initiateResponse = await axiosInstance.post(
    `api/ClientSelectionApproval/InitiateSignedDocumentUpload`,
    { fileName, contentType, fileSizeBytes: pdfBlob.size },
    { params: { token } },
  );
  const initiated = unwrap(initiateResponse, "Failed to prepare the signed document upload");

  await putFileToPresignedUrl(pdfBlob, initiated.data.uploadUrl, contentType);

  const dotIndex = fileName.lastIndexOf(".");
  const fileExtension = dotIndex >= 0 ? fileName.slice(dotIndex) : "";

  return { fileKey: initiated.data.fileKey, fileName, fileExtension, contentType };
};

// Confirms the uploaded document and locks every confirmed, unlocked item in this token's
// job/category as one batch. `signedDate` should be an ISO string (the date the client picked).
export const lockClientSelectionApproval = async (token, { fileKey, fileName, fileExtension, contentType, signedDate }) => {
  try {
    const response = await axiosInstance.post(
      `api/ClientSelectionApproval/Lock`,
      { fileKey, fileName, fileExtension, contentType, signedDate },
      { params: { token } },
    );
    const result = unwrap(response, "Failed to lock selections");
    return { success: true, message: result.message };
  } catch (error) {
    console.error("Error in lockClientSelectionApproval:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to lock selections",
    };
  }
};
